/**
 * The China Payment page: one page with a tab per request type. It replaced three pages — top-up,
 * proxy payment and transfer — whose addresses are kept only to redirect to the matching tab.
 */
export const CHINA_PAYMENT_ROUTE = '/china-payment/request';
export const LEGACY_CHINA_PAYMENT_ROUTES = {
  topUp: '/china-payment/top-up',
  proxyPayment: '/china-payment/proxy-payment',
  transfer: '/china-payment/transfer',
} as const;

/**
 * The customer's China Payment request history (MYP-69). Also where an ALIPAY notification opens:
 * the vendor sends one when a request changes, and this is the page that will list them. The list
 * itself waits on `getalipaylist`, which times out on the vendor's side (api-issues #26).
 */
export const CHINA_PAYMENT_RECORDS_ROUTE = '/china-payment/top-up-records';

/**
 * The sidebar tree, mirroring the existing MyPoz system's navigation. Data only, so App.tsx can
 * generate a route per child from the same source the menu renders from — the two cannot drift.
 *
 * Parents have no page of their own: they are disclosure buttons, and the children are the
 * destinations. A section with only one page is a plain link instead (MYP-133). `unreadBadge`
 * marks entries that show the live unread-notification count.
 *
 * Finance centre, Rebates & offers and Warehouse address moved to the profile menu in the header
 * (MYP-136) — see `ACCOUNT_MENU` below.
 */
export const NAV = [
  { to: '/dashboard', key: 'dashboard', icon: 'home' },
  {
    to: '/international',
    key: 'international',
    icon: 'language',
    children: [
      { to: '/international/packages', key: 'packages' },
      { to: '/international/orders', key: 'orders' },
    ],
  },
  {
    to: '/china-payment',
    key: 'chinaPayment',
    icon: 'swap_horiz',
    children: [
      { to: CHINA_PAYMENT_ROUTE, key: 'chinaPaymentCentre' },
      { to: CHINA_PAYMENT_RECORDS_ROUTE, key: 'topUpRecords' },
    ],
  },
] as const;

export type NavItem = (typeof NAV)[number];
export type NavGroupItem = Extract<NavItem, { children: ReadonlyArray<unknown> }>;
export type NavChild = NavGroupItem['children'][number];

/** Children that have a real page; everything else routes to the placeholder. */
export const NOTIFICATIONS_ROUTE = '/account/notifications';
export const WAREHOUSES_ROUTE = '/warehouse-address/list';

/**
 * The coupon list (MYP-71), the one page of 返利优惠 ("Rebates & offers"): My points and Rebate
 * details were removed, having no endpoint behind them. A plain menu link since MYP-133.
 */
export const COUPONS_ROUTE = '/coupons/vouchers';

/**
 * The account profile (MYP-72) — no sidebar entry: reached from the header's profile icon
 * (`Layout.tsx`) instead. The "account management" module it used to sit under (security centre,
 * linked accounts, contacts) was removed from the sidebar under MYP-85: a mentor review flagged
 * that module as never having been in scope, and asked for this one surviving screen to be
 * view-only and reached from the header rather than a sidebar entry.
 */
export const ACCOUNT_PROFILE_ROUTE = '/account/profile';

/** The wallet balance/freeze/points summary (MYP-54) — lands on the existing "Account balance" child. */
export const WALLET_BALANCE_ROUTE = '/wallet/balance';

/**
 * Where a customer lands after paying, failing or cancelling on Fiuu's FPX page (MYP-134):
 * `?orderid=…`, plus `&cancelled=1` from the cancel link. The vendor's Return handler sends the
 * customer here. No menu entry — it is only ever reached from Fiuu.
 */
export const WALLET_TOP_UP_RESULT_ROUTE = '/wallet/top-up/result';

/**
 * Creating an order is a flow, not a menu entry.
 *
 * The legacy system gives each of its stages a sidebar item — 添加包裹 / 提交运单 / 运单查询 /
 * 服务费用确认 — so a customer half way through an order can wander into another stage and lose it.
 * Here the stages are steps inside one page, reached from Orders, and the sidebar carries only the
 * two places you can actually browse to.
 */
export const ORDERS_ROUTE = '/international/orders';
export const NEW_ORDER_ROUTE = '/international/orders/new';

/** The Packages list, per MYP-31 — its own page rather than a tab inside Orders. */
export const PACKAGES_ROUTE = '/international/packages';

/** Route pattern for the edit flow; `editOrderRoute` builds the concrete path. */
export const EDIT_ORDER_ROUTE = '/international/orders/:orderNumber/edit';

/**
 * Link to an order's edit flow, optionally landing on the step that owns a section.
 *
 * The step travels as a query parameter rather than a second path segment: it is a view
 * preference, not a different resource, and a stale or mistyped one falls back to the first step
 * instead of a 404.
 */
export function editOrderRoute(orderNumber: string, step?: string): string {
  const path = `/international/orders/${encodeURIComponent(orderNumber)}/edit`;
  return step ? `${path}?step=${step}` : path;
}

/** One page of the account menu (MYP-136). */
export interface AccountMenuItem {
  readonly to: string;
  /** A key in the `nav` catalog. */
  readonly key:
    'myProfile' | 'accountBalance' | 'additionalPayment' | 'coupons' | 'warehouseAddress';
  readonly icon: string;
  /** The heading it sits under in the header's profile menu (`nav` catalog); none for My profile. */
  readonly group?: 'wallet' | 'more';
}

/**
 * The pages about the customer themselves (MYP-136): opened from the profile icon in the header,
 * and listed together on each of these pages as the "My account" menu, so one leads to the next.
 * They left the sidebar, which keeps the shipping and payment work. Their addresses did not change,
 * so notifications and old links still land on them.
 */
export const ACCOUNT_MENU: readonly AccountMenuItem[] = [
  { to: ACCOUNT_PROFILE_ROUTE, key: 'myProfile', icon: 'person' },
  {
    to: WALLET_BALANCE_ROUTE,
    key: 'accountBalance',
    icon: 'account_balance_wallet',
    group: 'wallet',
  },
  {
    to: '/wallet/additional-payment',
    key: 'additionalPayment',
    icon: 'request_quote',
    group: 'wallet',
  },
  { to: COUPONS_ROUTE, key: 'coupons', icon: 'redeem', group: 'more' },
  { to: WAREHOUSES_ROUTE, key: 'warehouseAddress', icon: 'location_on', group: 'more' },
];

/** The old sidebar section paths of the account pages, still sent to their first page. */
export const ACCOUNT_SECTION_REDIRECTS: readonly { from: string; to: string }[] = [
  { from: '/wallet', to: WALLET_BALANCE_ROUTE },
  { from: '/coupons', to: COUPONS_ROUTE },
  { from: '/warehouse-address', to: WAREHOUSES_ROUTE },
];

/** Whether a path is one of the account pages, which show the "My account" menu. */
export function isAccountPage(pathname: string): boolean {
  return ACCOUNT_MENU.some((item) => item.to === pathname);
}
